import { useState } from "react";
import { useFetch } from "../../../common/hooks/useFetch";
import Swal from "sweetalert2";
import { saveFile } from "../../../common/utils/saveFile.util";
import { createInvestigationRoadAccidentService } from "../services/createInvestigationRoadAccident.service";
import { updateInvestigationRoadAccidentService } from "../services/updateInvestigationRoadAccident.service";
import { deleteInvestigationRoadAccidentService } from "../services/deleteInvestigationRoadAccident.service";
import { exportToExcelInvestigationRoadAccidentService } from "../services/exportToExcelInvestigationRoadAccident.service";

const initialStateInvestigation = {
  accident: "",
  committeeAnalysisDate: "",
  directFatalityCosts: "",
  indirectFatalityCosts: "",
  directSevereInjuryCosts: "",
  indirectSevereInjuryCosts: "",
  directMinorInjuryCosts: "",
  indirectMinorInjuryCosts: "",
  directSimpleCollisionCosts: "",
  indirectSimpleCollisionCosts: "",
  accidentPaymentProof: "",
  injuryAgent: "",
  behaviorOrCondition: "",
  incidentMechanism: "",
  disabilityDays: "",
  injuryType: "",
  affectedBodyPart: "",
  correctiveAction: "",
  actionResponsible: "",
  actionExecutionDate: "",
  eventDetails: null,
};

export const useInvestigationAccidentCommand = (refresh) => {
  const fetchData = useFetch();
  const [errors, setErrors] = useState({ hasErrors: false, errors: {} });
  const [investigation, setInvestigation] = useState(initialStateInvestigation);

  const handleChangeInvestigation = ({ name, value }) => {
    setInvestigation((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const createInvestigationAccident = async (investigationAccident) => {
    try {
      const isValid = validations(investigationAccident);
      if (!isValid) return;
      const response = await createInvestigationRoadAccidentService(fetchData)(
        investigationAccident
      );

      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Investigación registrada",
        text: response.message,
      });
      setInvestigation(initialStateInvestigation);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al registrar la investigación",
      });
    }
  };

  const exportToExcel = async (filters) => {
    try {
      const response = await exportToExcelInvestigationRoadAccidentService(
        fetchData
      )(filters);
      saveFile(response, "investigaciones_siniestros_viales.xlsx");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error al exportar a Excel",
        text: error.message,
      });
    }
  };

  const updateInvestigationAccident = async (investigationAccident) => {
    try {
      const response = await updateInvestigationRoadAccidentService(fetchData)(investigationAccident);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Investigación actualizada",
        text: response.message,
      });
      setInvestigation(initialStateInvestigation);
      setErrors({ hasErrors: false, errors: {} });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Ocurrió un error al actualizar la investigación",
      });
    }
  };

  const deleteInvestigationAccident = async (id) => {
    try {
      const isSure = await Swal.fire({
        title: "¿Estás seguro?",
        text: "¿Deseas eliminar esta investigación?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!isSure.isConfirmed) return;
      await deleteInvestigationRoadAccidentService(fetchData)(id);
      refresh?.();
      Swal.fire({
        icon: "success",
        title: "Eliminado",
        text: "La investigación ha sido eliminada exitosamente",
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.message || "Ocurrió un error al eliminar la investigación",
      });
    }
  };

  const validations = (investigation) => {
    const newErrors = {};

    if (!investigation.accident) {
      newErrors.accident = "El siniestro es requerido";
    }

    if (!investigation.committeeAnalysisDate) {
      newErrors.committeeAnalysisDate =
        "La fecha del análisis del comité es requerida";
    }

    if (!investigation.directFatalityCosts) {
      newErrors.directFatalityCosts =
        "Los costos directos por fatalidades son requeridos";
    }

    if (!investigation.indirectFatalityCosts) {
      newErrors.indirectFatalityCosts =
        "Los costos indirectos por fatalidades son requeridos";
    }

    if (!investigation.directSevereInjuryCosts) {
      newErrors.directSevereInjuryCosts =
        "Los costos directos por lesiones graves son requeridos";
    }

    if (!investigation.indirectSevereInjuryCosts) {
      newErrors.indirectSevereInjuryCosts =
        "Los costos indirectos por lesiones graves son requeridos";
    }

    if (!investigation.directMinorInjuryCosts) {
      newErrors.directMinorInjuryCosts =
        "Los costos directos por lesiones leves son requeridos";
    }

    if (!investigation.indirectMinorInjuryCosts) {
      newErrors.indirectMinorInjuryCosts =
        "Los costos indirectos por lesiones leves son requeridos";
    }

    if (!investigation.directSimpleCollisionCosts) {
      newErrors.directSimpleCollisionCosts =
        "Los costos directos por choques simples son requeridos";
    }

    if (!investigation.indirectSimpleCollisionCosts) {
      newErrors.indirectSimpleCollisionCosts =
        "Los costos indirectos por choques simples son requeridos";
    }

    if (!investigation.accidentPaymentProof) {
      newErrors.accidentPaymentProof =
        "El comprobante de pago por accidente es requerido";
    }

    if (!investigation.injuryAgent) {
      newErrors.injuryAgent = "El agente de lesión es requerido";
    }

    if (!investigation.behaviorOrCondition) {
      newErrors.behaviorOrCondition =
        "El comportamiento o condición es requerido";
    }

    if (!investigation.incidentMechanism) {
      newErrors.incidentMechanism = "El mecanismo del siniestro es requerido";
    }

    if (!investigation.disabilityDays) {
      newErrors.disabilityDays = "Los días de incapacidad son requeridos";
    }

    if (!investigation.injuryType) {
      newErrors.injuryType = "El tipo de lesión es requerido";
    }

    if (!investigation.affectedBodyPart) {
      newErrors.affectedBodyPart = "La parte del cuerpo implicada es requerida";
    }

    if (!investigation.correctiveAction) {
      newErrors.correctiveAction = "La acción correctiva es requerida";
    }

    if (!investigation.actionResponsible) {
      newErrors.actionResponsible = "El responsable de la acción es requerido";
    }

    if (!investigation.actionExecutionDate) {
      newErrors.actionExecutionDate =
        "La fecha de ejecución de la acción es requerida";
    }

    if (!investigation.eventDetails) {
      newErrors.eventDetails = "La información del evento es requerida";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors({ hasErrors: true, errors: newErrors });
      return false;
    }

    return true;
  };

  return {
    investigation,
    handleChangeInvestigation,
    createInvestigationAccident,
    updateInvestigationAccident,
    deleteInvestigationAccident,
    exportToExcel,
    errors,
    setInvestigation,
  };
};
